import Phaser from 'phaser';

export class SoundManager {
  private scene: Phaser.Scene;
  private enabled: boolean = true;
  private volume: number = 0.3; // Lower volume for less intrusive sounds

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Player hit sound - low frequency thud
  public playPlayerHit(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 80;
    oscillator.type = 'sawtooth';

    gainNode.gain.setValueAtTime(this.volume * 0.5, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  }

  // Enemy killed sound - rising pitch
  public playEnemyKilled(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator.type = 'square';

    gainNode.gain.setValueAtTime(this.volume * 0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  }

  // XP collected sound - gentle chime
  public playXPCollected(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(this.volume * 0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  }

  // Level up sound - triumphant ascending notes
  public playLevelUp(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const notes = [523.25, 659.25, 783.99]; // C, E, G
    const noteDuration = 0.15;

    notes.forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'triangle';

      const startTime = audioContext.currentTime + (index * noteDuration);
      gainNode.gain.setValueAtTime(this.volume * 0.4, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + noteDuration);

      oscillator.start(startTime);
      oscillator.stop(startTime + noteDuration);
    });
  }

  // Power-up collected sound - magical sparkle
  public playPowerUpCollected(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const frequencies = [1000, 1200, 1400, 1600];
    const duration = 0.08;

    frequencies.forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      const startTime = audioContext.currentTime + (index * duration);
      gainNode.gain.setValueAtTime(this.volume * 0.25, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
  }

  // Weapon fire sound - quick zap
  public playWeaponFire(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.05);
    oscillator.type = 'sawtooth';

    gainNode.gain.setValueAtTime(this.volume * 0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.05);
  }

  // Boss spawn sound - ominous rumble
  public playBossSpawn(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(50, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + 0.5);
    oscillator.type = 'sawtooth';

    gainNode.gain.setValueAtTime(this.volume * 0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  }

  // Health pickup sound - healing tone
  public playHealthPickup(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 440;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(this.volume * 0.25, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  }

  // Game over sound - descending notes
  public playGameOver(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const notes = [523.25, 392, 329.63, 261.63]; // C, G, E, C (descending)
    const noteDuration = 0.25;

    notes.forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'triangle';

      const startTime = audioContext.currentTime + (index * noteDuration);
      gainNode.gain.setValueAtTime(this.volume * 0.35, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + noteDuration);

      oscillator.start(startTime);
      oscillator.stop(startTime + noteDuration);
    });
  }

  // Menu navigation sound - click
  public playMenuClick(): void {
    if (!this.enabled) return;

    const audioContext = (this.scene.game as any).sound?.context;
    if (!audioContext) return;

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 600;
    oscillator.type = 'square';

    gainNode.gain.setValueAtTime(this.volume * 0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.03);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.03);
  }
}
